<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import useAuthStore, { isPreAuthOnboardingToken, isUserToken } from '@/stores/authStore'
import { useAuth } from '@/composables/useAuth'
import { useAccess } from '@/composables/useAccess'
import { useQuery } from '@/composables/api'
import { useMenuItems } from './useMenu'
import getEnvironment from '@/utils/env'
import type { NavigationMenuItem } from './types'
import MainNav from './MainNav.vue'
import SubNav from './SubNav.vue'
import MobileDrawer from './MobileDrawer.vue'
import SidebarQuickNav from './SidebarQuickNav.vue'
import SidebarFavorites from './SidebarFavorites.vue'
import SidebarSettings from './SidebarSettings.vue'
import LanguageSelect from './LanguageSelect.vue'
import { isOneTrustReady, openCookieSettings } from '@/plugins/onetrust'
import logo from '@/assets/logo_white.png'
import logoBlack from '@/assets/logo_black.png'
import BSideDrawer from '@/components/base/BSideDrawer/BSideDrawer.vue'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'
import TokenSwitch from './TokenSwitch.vue'
import VismaNetTenantSwitch from './VismaNetTenantSwitch.vue'
import mdiMenu from '~icons/mdi/menu'
import mdiClose from '~icons/mdi/close'
import mdiMagnify from '~icons/mdi/magnify'
import mdiStar from '~icons/mdi/star'
import mdiCog from '~icons/mdi/cog'
import mdiLogout from '~icons/mdi/logout'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuth()
const authStore = useAuthStore()
const { canAccessResource } = useAccess()

const onboardingRouteId = computed(() =>
  typeof route.params.onboardingId === 'string' ? route.params.onboardingId : ''
)
const isStaffOnboardingView = computed(
  () =>
    route.name === 'CustomerOnboarding' &&
    isUserToken(auth.selectedToken.value?.payload) &&
    onboardingRouteId.value !== ''
)
const onboardingHeaderQuery = useQuery(
  '/onboardings/{resource_id}',
  computed(() => ({ path: { resource_id: onboardingRouteId.value } })),
  { enabled: isStaffOnboardingView, meta: { notifyOnError: false } }
)
const onboardingCompanyName = computed(() => onboardingHeaderQuery.data.value?.account_name ?? '')

const canAccessImportFilters = canAccessResource('partner--import-filters')

const enabledImportFiltersQuery = useQuery(
  '/partner--import-filters',
  { query: { where: { is_enabled: true }, max_results: 1 } },
  {
    meta: { notifyOnError: false },
    enabled: computed(
      () =>
        auth.isAuthenticated.value &&
        !auth.isSolicitor.value &&
        !auth.isSystemUser.value &&
        canAccessImportFilters.value
    ),
  }
)
const vismaNetRegistrationDataQuery = useQuery(
  '/visma-net-registration--data',
  {},
  {
    meta: { notifyOnError: false },
    enabled: computed(
      () =>
        auth.isAuthenticated.value &&
        !auth.isSolicitor.value &&
        !auth.isSystemUser.value &&
        canAccessImportFilters.value
    ),
  }
)

const hasNoEnabledImportFilter = computed(() => {
  const registrationData = vismaNetRegistrationDataQuery.data.value
  if (registrationData == null || registrationData.creditors_data.length === 0) return false
  const importFilterData = enabledImportFiltersQuery.data.value
  if (importFilterData?._items == null) return false
  return importFilterData._items.length === 0
})

const isOnVismaNetSettingsPage = computed(() => {
  const routeName = route.name
  return routeName === 'VismaNetSettings' || routeName === 'OnboardingCompleted'
})

const tooltipDismissed = ref(false)

const showImportFilterTooltip = computed(
  () => hasNoEnabledImportFilter.value && !isOnVismaNetSettingsPage.value && !tooltipDismissed.value
)

const isRouterReady = ref(false)
void router.isReady().then(() => {
  isRouterReady.value = true
})

const minimalHeader = computed(() => route.meta.minimalHeader === true)
const isPreAuthOnboardingCustomer = computed(() => isPreAuthOnboardingToken(auth.selectedToken.value?.payload))
const showHeader = computed(
  () =>
    isRouterReady.value &&
    route.meta.fullscreen !== true &&
    (auth.isAuthenticated.value || (minimalHeader.value && isPreAuthOnboardingCustomer.value))
)
const { lgAndUp } = useDisplay()
const isMobile = computed(() => !lgAndUp.value)
const env = getEnvironment()
const { menu, isReady } = useMenuItems()
const drawer = ref(false)

type DialogType = 'search' | 'favorites' | 'settings'
const activeDialog = ref<DialogType>()
const sidebarSettingsRef = useTemplateRef<InstanceType<typeof SidebarSettings>>('sidebarSettings')

const selectedMainItem = ref<NavigationMenuItem>()

/** Param names defined in the current route's path pattern (e.g. 'channel' for /tickets/:channel) */
const routeDefinedParams = computed(() => {
  const names = new Set<string | undefined>()
  for (const record of route.matched) {
    for (const match of record.path.matchAll(/:(\w+)/g)) {
      names.add(match[1])
    }
  }
  return names
})

function menuMatchesRoute(menuItem: NavigationMenuItem): boolean {
  const menuParams = menuItem.params ?? {}
  const currentRouteName = route.name != null ? String(route.name) : null

  if (menuItem.name != null && currentRouteName != null) {
    if (menuItem.name === currentRouteName) {
      const paramsMatch = Object.entries(menuParams).every(
        ([key, value]) => !routeDefinedParams.value.has(key) || route.params[key] === value
      )
      if (paramsMatch) return true
    }
  }

  if (currentRouteName === 'ItemDisplay' && menuParams.resource != null) {
    if (route.params.resource === menuParams.resource) {
      return true
    }
  }

  if (currentRouteName === 'Ticket' && menuItem.name === 'Tickets' && menuParams.channel != null) {
    if (route.params.channel === menuParams.channel) {
      return true
    }
  }

  if (menuItem.sub_menu != null) {
    for (const subItem of menuItem.sub_menu) {
      if (menuMatchesRoute(subItem)) return true
    }
  }

  if (menuItem.list_menu != null) {
    for (const listItem of menuItem.list_menu) {
      if (menuMatchesRoute(listItem)) return true
    }
  }

  return false
}

function findMatchingMainMenuItem() {
  for (const mainItem of menu.value) {
    if (mainItem.locale_name === 'common.other') continue
    if (menuMatchesRoute(mainItem)) return mainItem
  }
  return undefined
}

const activeSubMenuItem = computed(() => {
  const subMenu = selectedMainItem.value?.sub_menu
  if (subMenu == null || subMenu.length === 0) return undefined
  return subMenu.find((subItem: NavigationMenuItem) => menuMatchesRoute(subItem))
})

function handleMainItemSelect(item: NavigationMenuItem) {
  const isOther = item.locale_name === 'common.other'
  const isSameAsSelected = selectedMainItem.value?.locale_name === item.locale_name

  if (isOther) return

  if (isSameAsSelected) return

  selectedMainItem.value = item
}

function undoImpersonation() {
  const token = authStore.selectedToken
  if (token == null) return

  auth.switchToSignedInByToken()
  authStore.removeToken(token)
}

watch(isMobile, (isMobile) => {
  if (isMobile) {
    selectedMainItem.value = undefined
  } else {
    drawer.value = false
    selectedMainItem.value = findMatchingMainMenuItem()
  }
})

watch(
  [() => route.fullPath, () => menu.value],
  () => {
    if (isMobile.value) return
    selectedMainItem.value = findMatchingMainMenuItem()
  },
  { immediate: true }
)
</script>

<template>
  <div v-if="showHeader">
    <v-app-bar app>
      <div class="d-flex justify-space-between align-center w-100 h-100 px-6 max-width-header">
        <div class="d-flex align-center ga-3 h-100">
          <v-app-bar-nav-icon
            v-if="!minimalHeader"
            class="d-flex d-lg-none"
            :aria-label="t('common.navigation_menu')"
            @click="drawer = !drawer"
          >
            <v-icon :icon="drawer ? mdiClose : mdiMenu" />
          </v-app-bar-nav-icon>

          <div class="d-flex align-center flex-shrink-0 pr-3">
            <router-link :to="{ name: 'Dashboard' }">
              <v-img
                :src="logo"
                alt="Amili"
                height="24"
                width="70"
                class="m-0 p-0 cursor-pointer"
              />
            </router-link>
          </div>

          <main-nav
            v-if="!isMobile && !minimalHeader"
            class="d-none d-lg-flex flex-grow-1"
            :items="isReady ? menu : []"
            :active-item="selectedMainItem?.locale_name"
            @select="handleMainItemSelect"
          />
        </div>

        <div class="d-flex align-center ga-3 h-100">
          <v-chip
            v-if="env != null && env.id !== 'production'"
            class="text-uppercase"
            :color="env.color ?? undefined"
            variant="tonal"
          >
            {{ env.name }}
          </v-chip>

          <visma-net-tenant-switch />
          <token-switch v-if="!isStaffOnboardingView" />

          <v-chip
            v-if="isUserToken(auth.selectedToken.value?.payload) && auth.selectedToken.value?.payload.signed_in_by_u_id"
            :text="t('common.as_x', { x: auth.selectedToken.value?.payload.u_name })"
            :prepend-icon="mdiLogout"
            color="orange"
            max-width="300"
            variant="flat"
            @click="undoImpersonation()"
          />

          <v-chip
            v-if="isStaffOnboardingView"
            :text="
              onboardingCompanyName !== ''
                ? t('common.onboarding_for_x', { x: onboardingCompanyName })
                : t('common.onboarding')
            "
            color="brand-primary"
            max-width="300"
            variant="flat"
          />

          <div class="d-flex align-center ml-4 ga-3">
            <b-tooltip-btn
              v-if="!minimalHeader"
              :icon="mdiMagnify"
              density="comfortable"
              tooltip="search"
              @click="activeDialog = 'search'"
            />
            <b-tooltip-btn
              v-if="!minimalHeader"
              :icon="mdiStar"
              density="comfortable"
              class="d-none d-lg-flex"
              tooltip="watch_list"
              @click="activeDialog = 'favorites'"
            />
            <v-tooltip
              :model-value="showImportFilterTooltip"
              location="bottom end"
              content-class="settings-tooltip bg-brand-primary"
              max-width="350"
              offset="20"
              persistent
            >
              <template #activator="{ props: calloutProps }">
                <b-tooltip-btn
                  :ref="calloutProps.ref"
                  :aria-describedby="calloutProps['aria-describedby']"
                  :icon="mdiCog"
                  tooltip="settings"
                  density="comfortable"
                  :class="minimalHeader ? 'd-flex' : 'd-none d-lg-flex'"
                  @click="activeDialog = 'settings'"
                  @mouseenter="tooltipDismissed = true"
                />
              </template>
              <div class="settings-tooltip-content">
                <div class="settings-tooltip-arrow" />
                <span>{{ t('common.no_partner_import_filter_is_enabled_tooltip') }}</span>
              </div>
            </v-tooltip>
          </div>
        </div>
      </div>

      <template
        v-if="isReady && selectedMainItem?.sub_menu?.length > 0"
        #extension
      >
        <sub-nav
          :items="selectedMainItem?.sub_menu || []"
          :active-item="activeSubMenuItem?.locale_name"
          @close="selectedMainItem = undefined"
        />
      </template>
    </v-app-bar>

    <b-side-drawer
      v-model="drawer"
      location="left"
    >
      <template #title>
        <div class="d-flex align-center flex-shrink-0">
          <v-img
            :src="logoBlack"
            alt="Amili"
            height="24"
            width="70"
            class="m-0 p-0"
          />
          <span class="d-sr-only">{{ t('common.menu') }}</span>
        </div>
      </template>

      <mobile-drawer
        v-model:drawer="drawer"
        :items="isReady ? menu : []"
        @select="handleMainItemSelect"
        @open:starred-items="activeDialog = 'favorites'"
        @open:settings="activeDialog = 'settings'"
        @logout="auth.signOut"
      />
    </b-side-drawer>

    <b-side-drawer
      :model-value="activeDialog === 'search'"
      :title="t('common.search_menu')"
      @update:model-value="activeDialog = $event ? 'search' : undefined"
    >
      <sidebar-quick-nav @close="activeDialog = undefined" />
    </b-side-drawer>

    <b-side-drawer
      :model-value="activeDialog === 'favorites'"
      :title="t('common.watch_list')"
      @update:model-value="activeDialog = $event ? 'favorites' : undefined"
    >
      <sidebar-favorites />
    </b-side-drawer>

    <b-side-drawer
      :model-value="activeDialog === 'settings'"
      :title="t('common.settings')"
      @update:model-value="activeDialog = $event ? 'settings' : undefined"
      @close="sidebarSettingsRef?.flushSettings()"
    >
      <sidebar-settings
        v-if="!isPreAuthOnboardingCustomer"
        ref="sidebarSettings"
      />
      <language-select
        v-else
        class="px-4"
      />
      <template #footer>
        <div class="d-flex justify-center align-center w-100 position-relative">
          <v-btn
            v-if="isOneTrustReady"
            variant="text"
            size="small"
            class="text-decoration-underline position-absolute"
            style="left: 0"
            @click="openCookieSettings"
          >
            {{ t('common.cookies') }}
          </v-btn>
          <v-btn
            variant="outlined"
            @click="auth.signOut"
          >
            {{ t('common.logout') }}
          </v-btn>
        </div>
      </template>
    </b-side-drawer>
  </div>
</template>

<style scoped>
.settings-tooltip.v-overlay__content {
  overflow: visible !important;
}

.settings-tooltip-content {
  position: relative;
}

.settings-tooltip-arrow {
  width: 15px;
  height: 15px;
  background-color: rgb(var(--v-theme-brand-primary));
  position: absolute;
  right: -6px;
  top: -12px;
  transform: rotate(45deg);
}
</style>
